'use client'

import Image from 'next/image'
import type { ParseKeys } from 'i18next'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { setI18nNamespace, tKey } from '@/utils/i18n'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { type FlowProps } from '../flows.types'
import { InsuranceShowcaseCard } from './InsuranceShowcaseCard'
import { cardsContainer, header } from './Flow03Insurance.css'

setI18nNamespace('appShowcase')

type AppShowcaseKey = ParseKeys<'appShowcase'>

type CardConfig = {
  id: string
  titleKey: AppShowcaseKey
  subLabel: string
  pillowSrc: string
  tierKey: AppShowcaseKey
  statusKey: AppShowcaseKey
}

const CARDS: Array<CardConfig> = [
  {
    id: 'home',
    titleKey: tKey<'appShowcase'>('FLOW_03_CARD_HOME_TITLE'),
    subLabel: 'Skaraborgsgatan 8',
    pillowSrc: '/images/pillows/homeowner-pillow-832x832px.png',
    tierKey: tKey<'appShowcase'>('FLOW_03_CARD_HOME_TIER'),
    statusKey: tKey<'appShowcase'>('FLOW_03_CARD_STATUS_ACTIVE'),
  },
  {
    id: 'car',
    titleKey: tKey<'appShowcase'>('FLOW_03_CARD_CAR_TITLE'),
    subLabel: 'OPS 374',
    pillowSrc: '/images/pillows/car-pillow-832x832px.png',
    tierKey: tKey<'appShowcase'>('FLOW_03_CARD_CAR_TIER'),
    statusKey: tKey<'appShowcase'>('FLOW_03_CARD_STATUS_ACTIVE'),
  },
  {
    id: 'dog',
    titleKey: tKey<'appShowcase'>('FLOW_03_CARD_DOG_TITLE'),
    subLabel: 'Toto',
    pillowSrc: '/images/pillows/dog-pillow-832x832px.png',
    tierKey: tKey<'appShowcase'>('FLOW_03_CARD_DOG_TIER'),
    statusKey: tKey<'appShowcase'>('FLOW_03_CARD_STATUS_ACTIVE'),
  },
]

const spring = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 30,
  mass: 0.8,
}

export function Flow03Insurance(_props: FlowProps) {
  const { t } = useTranslation('appShowcase')

  return (
    <div className={yStack({ height: '100%' })}>
      <div className={header}>
        <Text as="span" size="sm" color="textPrimary">
          {t('FLOW_03_HEADER')}
        </Text>
      </div>

      <div className={cardsContainer}>
        {CARDS.map((card, index) => (
          <motion.div
            key={card.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.3 + index * 0.15 }}
          >
            <InsuranceShowcaseCard
              title={t(card.titleKey)}
              subLabel={card.subLabel}
              pillowSrc={card.pillowSrc}
              tier={t(card.tierKey)}
              status={t(card.statusKey)}
            />
          </motion.div>
        ))}
      </div>

      <div className={sprinkles({ mt: 'auto', paddingInline: 'xs', paddingBottom: 'xxs' })}>
        <Image
          src="/images/dock.png"
          alt={t('FLOW_03_NAV_ALT')}
          width={804}
          height={230}
          className={sprinkles({ display: 'block' })}
        />
      </div>
    </div>
  )
}
